import { personalInfo } from './personalInfo';

export type SeoLanguage = 'en' | 'es';

export const SITE_URL = 'https://n33no.online';
export const SITE_NAME = 'n33no';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-image.jpg`;
export const PORTRAIT_IMAGE = `${SITE_URL}/images/me/n33no-portrait.jpg`;

type SeoCopy = {
  title: string;
  description: string;
  keywords: string;
  ogLocale: string;
};

const homeCopy: Record<SeoLanguage, SeoCopy> = {
  en: {
    title: 'n33no | Frontend Developer — React & TypeScript',
    description:
      personalInfo.description ||
      'Frontend Developer specialized in React, TypeScript and modern web technologies. Creating exceptional digital experiences.',
    keywords:
      'n33no, frontend developer, react, typescript, javascript, portfolio, web development, UI UX, Montevideo, Uruguay, desarrollador frontend',
    ogLocale: 'en_US',
  },
  es: {
    title: 'n33no | Desarrollador Frontend — React & TypeScript',
    description:
      'Desarrollador Frontend especializado en React, TypeScript y tecnologías web modernas. Creando experiencias digitales excepcionales.',
    keywords:
      'n33no, desarrollador frontend, react, typescript, javascript, portfolio, desarrollo web, UI UX, Montevideo, Uruguay, frontend developer',
    ogLocale: 'es_ES',
  },
};

const privacyCopy: Record<SeoLanguage, SeoCopy> = {
  en: {
    title: 'Privacy Policy | n33no',
    description: 'Privacy policy for the n33no portfolio website.',
    keywords: 'privacy policy, n33no, data protection',
    ogLocale: 'en_US',
  },
  es: {
    title: 'Política de Privacidad | n33no',
    description: 'Política de privacidad del sitio portfolio de n33no.',
    keywords: 'política de privacidad, n33no, protección de datos',
    ogLocale: 'es_ES',
  },
};

export const getHomeSeo = (language: SeoLanguage): SeoCopy => homeCopy[language];

export const getPageSeo = (pathname: string, language: SeoLanguage): SeoCopy & { canonicalPath: string } => {
  if (pathname.startsWith('/privacy-policy-es')) {
    return { ...privacyCopy.es, canonicalPath: '/privacy-policy-es' };
  }
  if (pathname.startsWith('/privacy-policy-en') || pathname.startsWith('/privacy-policy')) {
    return { ...privacyCopy.en, canonicalPath: pathname.startsWith('/privacy-policy-en') ? '/privacy-policy-en' : '/privacy-policy' };
  }

  const home = getHomeSeo(language);
  return {
    ...home,
    canonicalPath: language === 'es' ? '/?lang=es' : '/',
  };
};

export const absoluteUrl = (path: string): string => {
  if (path.startsWith('http')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${normalized === '/' ? '/' : normalized}`;
};

export const buildPersonJsonLd = (language: SeoLanguage) => {
  const seo = getHomeSeo(language);
  const sameAs = [personalInfo.social.github, personalInfo.social.linkedin, personalInfo.social.twitter].filter(
    Boolean,
  ) as string[];

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: `${SITE_URL}/`,
        name: SITE_NAME,
        alternateName: ['aarjona1991', personalInfo.fullName],
        description: seo.description,
        inLanguage: ['en', 'es'],
        publisher: { '@id': `${SITE_URL}/#person` },
      },
      {
        '@type': 'Person',
        '@id': `${SITE_URL}/#person`,
        name: personalInfo.fullName,
        alternateName: [SITE_NAME, 'aarjona1991', personalInfo.name],
        jobTitle: personalInfo.title,
        description: seo.description,
        url: `${SITE_URL}/`,
        image: {
          '@type': 'ImageObject',
          url: PORTRAIT_IMAGE,
          contentUrl: PORTRAIT_IMAGE,
        },
        email: personalInfo.email,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Montevideo',
          addressCountry: 'UY',
        },
        sameAs,
        knowsAbout: [
          'React',
          'TypeScript',
          'JavaScript',
          'Frontend Development',
          'Web Development',
          'UI/UX Design',
          'Responsive Design',
          'Tailwind CSS',
        ],
        worksFor: {
          '@type': 'Organization',
          name: 'Freelance',
        },
      },
      {
        '@type': 'ProfilePage',
        '@id': `${SITE_URL}/#profilepage`,
        url: `${SITE_URL}/`,
        name: seo.title,
        description: seo.description,
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: { '@id': `${SITE_URL}/#person` },
        mainEntity: { '@id': `${SITE_URL}/#person` },
        inLanguage: language,
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: DEFAULT_OG_IMAGE,
        },
      },
    ],
  };
};
